import View from "../framework/view";
import Server from "../server";
import template from "../../pugc/reports";
import jQuery from "jquery";
import {formToJson, plural, renderDollars, renderTimeInterval} from "../misc/utils";
import {AusDollar, JsonDate} from "../misc/types";
import {toSalesData} from "../bo/sales";
import {Order} from "../bo/order";
import {User} from "../bo/user";
import Alerts from "../misc/alerts";
import {formatDate, now, todayJSONDate, toJSONDate} from "../misc/dates";
import {isCancelled} from "../misc/orders";
import {HeatmapView} from "./heatmap";
import {ChartData} from "../bo/chart_data";
import {createAjaxOptions, renderDisposably} from "./chart_helper";
import {addDays} from "date-fns";

type CummulativeType = "dollars" | "orders";

export class ReportsView extends View {

    static classname = "reports-view";

    private readonly server = Server.instance();

    from: JsonDate | undefined = toJSONDate(addDays(now(), -7));
    to: JsonDate | undefined = todayJSONDate();

    user: User;

    deliveriesData: any = undefined;

    deliveryDetailsData : any = undefined;

    salesData: any = undefined;

    salesTitle: any = undefined;

    expandProducts: { [product: string] : boolean } = {};

    orders: Order[] | undefined;

    authenticated: boolean = false;

    heatmap? : HeatmapView;

    chartData?: ChartData;

    chartParams: ChartParams = {
        showLegend: false,
        onlyLines: false,
        noYAxisTitles: true,
    };

    googleAdsData: any = undefined;

    cummulativeSaleData: any = undefined;

    constructor(user: User) {
        super(ReportsView.classname);
        this.user = user;
    }

    protected render0(): JQuery<HTMLElement> {

        const renderPrice = (x : AusDollar | undefined ) => x ? renderDollars(x) : "";

        const data = {
            deliveriesData: this.deliveriesData,
            deliveryDetailsData: this.deliveryDetailsData,
            salesData: this.salesData,
            salesTitle: this.salesTitle,
            heatmap: this.heatmap,
            renderDollars: renderPrice,
            username: this.user.username,
            authenticated: this.authenticated,
            formatDate: formatDate,
            from: this.from,
            to: this.to,
            renderTimeInterval: renderTimeInterval,
            chartData: this.chartData,
            googleAds: this.googleAdsData,
            cummulativeSaleData: this.cummulativeSaleData,
        };

        const out = this.pug(template, data);

        out.find(".with-validation").validate({
            rules: {
                from: "required",
                to: "required",
            }
        });

        this.afterUpdate(() => {
            this.elt().find("input.datefield").datefield({startDay: 1});

            if (this.chartData) {
                renderChart(this.elt(), this.chartData, this.chartParams, false);
            }
        });

        return out;
    }

    getDates() : { from: JsonDate, to: JsonDate } | false {
        const form = this.elt().find("form.report-form");

        if (! form.valid()) {
            return false;
        }
        const data : any= {};
        formToJson(form, data);

        this.from = data.from as JsonDate;
        this.to = data.to as JsonDate;

        return {
            from: data.from as JsonDate,
            to: data.to as JsonDate
        }

    }

    deliveries() {
        const dates = this.getDates();
        if (! dates) {
            return;
        }

        const report_ = this.server.getDeliveryReport(dates.from, dates.to);

        report_.then((report) => {
            this.deliveriesData = report;
            this.update(".results");
        })
    }

    deliveryDetails() {
        const dates = this.getDates();
        if (! dates) {
            return;
        }
        const report_ = this.server.getDeliveryDetailsReport(dates.from, dates.to);

        report_.then((report) => {
            this.deliveryDetailsData = report;
            this.update(".results");
        })
    }

    async sales(useDeliveryDate: boolean) {
        const dates = this.getDates();
        if (! dates) {
            return;
        }
        const orders = await this.server.getOrdersByDate(dates.from, dates.to, useDeliveryDate);

        this.orders = orders.filter((x) => ! isCancelled(x));

        const title = useDeliveryDate ? "Order summary" : "Sales summary";
        this.reportSales(title);
    }

    async bestSellers(useDeliveryDate: boolean) {
        const dates = this.getDates();
        if (! dates) {
            return;
        }
        const orders = await this.server.getOrdersByDate(dates.from, dates.to, useDeliveryDate);

        this.orders = orders.filter(o => o.id && ! o.id.startsWith("manual"));

        const title = "Best sellers by " + (useDeliveryDate ? "order date" : "sales date");
        this.reportBestSellers(title);
    }

    async weeklySales(onlineOnly: boolean) {
        const dates = this.getDates();
        if (! dates) {
            return;
        }
        this.chartData = await this.server.getWeeklySalesReport(dates.from, dates.to, onlineOnly);
        this.elt().find(".chartelt").data("initted", false);
        this.update(".chartelt");
    }

    async cummulativeSales(type: CummulativeType) {
        const dates = this.getDates();
        if (! dates) {
            return;
        }
        this.chartData = await this.server.getCummulativeSalesReport(dates.from, dates.to, type);
        this.chartParams = {
            showLegend: true,
            onlyLines: true,
            noYAxisTitles: false,
        };
        this.elt().find(".chartelt").data("initted", false);

        const series = this.chartData?.series || [];
        const years = series.map(s => s.axisName as string) || [];

        const renderers = series.map(s => {
             if (s.axisUnits == "$") {
                 return renderDollars;
             } else {
                 return (x: any) => x;
             }
        });

        const daysIndex: any = {};
        const ticks = this.chartData?.ticks || [];
        for (let i = 0; i < ticks.length; i++) {
            daysIndex[ticks[i]] = i;
        }

        const days : any = this.chartData?.ticks?.filter(d => d.endsWith("23:00"));

        const data : any = {};
        for (let y = 0; y < years.length; y++) {
            const values : any = {};
            data[years[y]] = values;
            for (const day of days) {
                const i = daysIndex[day];
                // @ts-ignore
                const v : any = series[y]["values"][i];
                const r = renderers[y](v);
                values[day] = r;
            }
        }

        this.cummulativeSaleData = {
            years: years,
            days: days,
            data: data,
        };

        this.update([".chartelt", ".cummulative-sales"]);
    }

    async reportSales(title: string) {
        if (! this.orders) {
            return;
        }
        const salesData = await toSalesData(this.orders, this.expandProducts);
        this.salesData = salesData;
        this.salesTitle = title;
        this.update(".results");
    }

    async reportBestSellers(title: string) {
        if (! this.orders) {
            return;
        }
        const salesData = await toSalesData(this.orders, this.expandProducts);
        salesData.products = salesData.products.sort((a, b) => {

            function cmp(x: number, y:number) {
                if (x == y) {
                    return 0;
                }
                return x < y ? -1 : 1;
            }

            let x = cmp(b.count, a.count);
            if (x == 0) {
                x = cmp(b.sales, a.sales);
            }
            return x;
        });

        this.salesData = salesData;
        this.salesTitle = title;

        this.update(".results");
    }

    async googleAds() {
        const dates = this.getDates();
        if (! dates) {
            return;
        }
        this.googleAdsData = await this.server.getGoogleAdsData(dates.from, dates.to);
        this.update(".results");
    }

    expandProduct(product: string) {
        this.expandProducts[product] = true;
        this.reportSales(this.salesTitle);
    }

    async authenticate() {
        const form = this.elt().find("form.authenticate-form");

        if (! form.valid()) {
            return false;
        }
        const data : any= {};
        formToJson(form, data);

        const login = await this.server.login({username: this.user.username, password: data.x});
        if (login.username == this.user.username) {
            this.authenticated = true;
            this.update();
        } else {
            Alerts.alert("Incorrect password");
        }
    }

    async showHeatmap() {
        const dates = this.getDates();
        if (! dates) {
            return;
        }
        const orders = await this.server.getOrdersByDate(dates.from, dates.to);
        this.heatmap = new HeatmapView();
        this.heatmap.setOrders(orders);
        this.update(".results");
    }

}

const seriesColours = ['rgb(0, 0, 255)', 'rgb(173, 216, 230)', '#546E7A' ];

type ChartParams = {
    showLegend: boolean,
    onlyLines: boolean,
    noYAxisTitles: boolean,
}

export function renderChart(elt: JQuery<HTMLElement>, chartData: ChartData, p: ChartParams, animation: boolean) {

    const container = elt.find(".chartelt");

    if (container.length && chartData && ! container.data("initted")) {

        container.data("initted", true);

        console.log("=> running chart on " + chartData.title);

        const params = {
            chartData: chartData,
            seriesColours: seriesColours,
            height: "auto",
            showLegend: p.showLegend,
            onlyLines: p.onlyLines,
            noYAxisTitles: p.noYAxisTitles,
        };

        const opts = createAjaxOptions(params);
        return renderDisposably(container[0], opts, () => {
            // container is no longer in the DOM, but as a matter of good programming we de-init it
            // in case it gets re-inserted somehow.
            container.data("initted", false);
        });
    }
}

interface Legend {
    title: string,
    values: Array<string | number | undefined>,
    icon?: string,
    color: string
}



View.on(
    "." + ReportsView.classname,
    "click",
    "button.confirm-password",
    function(that: ReportsView, event, elt) {
        event.preventDefault();
        that.authenticate();
    });

View.on(
    "." + ReportsView.classname,
    "click",
    ".deliveries",
    function(that: ReportsView, event, elt) {
        event.preventDefault();
        that.deliveries();
    });

View.on(
    "." + ReportsView.classname,
    "click",
    ".delivery-dtls",
    function(that: ReportsView, event, elt) {
        event.preventDefault();
        that.deliveryDetails();
    });

View.on(
    "." + ReportsView.classname,
    "click",
    ".sales",
    function(that: ReportsView, event, elt) {
        event.preventDefault();
        that.sales(false);
    });

View.on(
    "." + ReportsView.classname,
    "click",
    ".weeklySales",
    function(that: ReportsView, event, elt) {
        event.preventDefault();
        that.weeklySales(false);
    });

View.on(
    "." + ReportsView.classname,
    "click",
    ".weeklyOnlineSales",
    function(that: ReportsView, event, elt) {
        event.preventDefault();
        that.weeklySales(true);
    });

View.on(
    "." + ReportsView.classname,
    "click",
    ".cummulativeSales",
    function(that: ReportsView, event, elt) {
        event.preventDefault();
        const type = jQuery(elt).data("type") as CummulativeType;
        that.cummulativeSales(type);
    });

View.on(
    "." + ReportsView.classname,
    "click",
    ".orders",
    function(that: ReportsView, event, elt) {
        event.preventDefault();
        that.sales(true);
    });

View.on(
    "." + ReportsView.classname,
    "click",
    ".best-sellers",
    function(that: ReportsView, event, elt) {
        event.preventDefault();
        const useDeliveryDate = jQuery(elt).data("usedelivery") ? true : false;
        that.bestSellers(useDeliveryDate);
    });

View.on(
    "." + ReportsView.classname,
    "click",
    ".action.wrbGoogleAds",
    function(that: ReportsView, event, elt) {
        event.preventDefault();
        that.googleAds();
    });

View.on(
    "." + ReportsView.classname,
    "click",
    ".heatmap",
    function(that: ReportsView, event, elt) {
        event.preventDefault();
        that.showHeatmap();
    });

View.on(
    "." + ReportsView.classname,
    "click",
    ".expand-product",
    function(that: ReportsView, event, elt) {
        event.preventDefault();
        const product = jQuery(elt).data("product") as string;
        that.expandProduct(product);
    });

